import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { storage } from "@/lib/storage";
import type { WrongAnswerRecord } from "@/lib/storage";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, XCircle, Eye, EyeOff, BookOpen } from "lucide-react";
import MathText from "@/components/MathText";
import ModuleLockedState from "@/components/ModuleLockedState";
import { useModuleStatus } from "@/hooks/use-module-status";
import { useProfile } from "@/hooks/use-profile";
import { useRolePerspective } from "@/hooks/use-role-perspective";

interface WrongAnswer extends WrongAnswerRecord {
  topicName: string;
  topicId: string;
  timestamp: number;
}

export default function Review() {
  const [, setLocation] = useLocation();
  const [wrongAnswers, setWrongAnswers] = useState<WrongAnswer[]>([]);
  const [revealedAnswers, setRevealedAnswers] = useState<Set<number>>(new Set());
  const [showAllAnswers, setShowAllAnswers] = useState(false);
  const { profile } = useProfile();
  const { effectiveRole } = useRolePerspective(profile?.id, profile?.role);
  const { status: moduleStatus, loading: moduleStatusLoading } = useModuleStatus('review');

  useEffect(() => {
    const wrong = storage.getWrongAnswers();
    setWrongAnswers(wrong);
  }, []);

  useEffect(() => {
    if (!moduleStatusLoading && moduleStatus === 'hidden' && effectiveRole !== 'admin') {
      setLocation('/');
    }
  }, [effectiveRole, moduleStatus, moduleStatusLoading, setLocation]);

  if (!moduleStatusLoading && moduleStatus && moduleStatus !== 'active' && moduleStatus !== 'hidden' && effectiveRole !== 'admin') {
    return <ModuleLockedState status={moduleStatus} />;
  }

  if (!moduleStatusLoading && moduleStatus === 'hidden' && effectiveRole !== 'admin') {
    return null;
  }

  const toggleRevealAnswer = (index: number) => {
    setRevealedAnswers(prev => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  const revealAllAnswers = () => {
    setShowAllAnswers(true);
    setRevealedAnswers(new Set(wrongAnswers.map((_, i) => i)));
  };

  const hideAllAnswers = () => {
    setShowAllAnswers(false);
    setRevealedAnswers(new Set());
  };

  const handlePracticeQuiz = () => {
    // Create a custom quiz with the wrong answer topics
    const topicIds = Array.from(new Set(wrongAnswers.map(wa => wa.topicId)));
    if (topicIds.length > 0) {
      setLocation(`/custom-quiz/start?topics=${topicIds.join(',')}&count=10&difficulty=foundation`);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold font-serif">Review Wrong Answers</h1>
              <p className="text-muted-foreground mt-1">
                Learn from your mistakes to improve
              </p>
            </div>
            <Button onClick={() => setLocation("/")} data-testid="button-back-home">
              Back to Quizzes
            </Button>
          </div>

          {wrongAnswers.length === 0 ? (
            <Card className="p-12 text-center">
              <CheckCircle2 className="h-16 w-16 text-green-500 mx-auto mb-4" />
              <h3 className="text-xl font-bold mb-2">Perfect Record!</h3>
              <p className="text-muted-foreground">
                You haven't gotten any questions wrong yet. Keep up the great work!
              </p>
            </Card>
          ) : (
            <>
              {/* Action Buttons */}
              <div className="flex flex-wrap gap-3 justify-end">
                <Button
                  variant="outline"
                  onClick={showAllAnswers ? hideAllAnswers : revealAllAnswers}
                >
                  {showAllAnswers ? (
                    <>
                      <EyeOff className="h-4 w-4 mr-2" />
                      Hide All Answers
                    </>
                  ) : (
                    <>
                      <Eye className="h-4 w-4 mr-2" />
                      Show All Answers
                    </>
                  )}
                </Button>
                <Button onClick={handlePracticeQuiz}>
                  <BookOpen className="h-4 w-4 mr-2" />
                  Practice Quiz from Wrong Answers
                </Button>
              </div>

              <div className="space-y-4">
                {wrongAnswers.map((item, idx) => {
                  const isRevealed = revealedAnswers.has(idx);
                  
                  return (
                    <Card key={idx} className="p-6">
                      <div className="space-y-4">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <Badge variant="outline" className="mb-2">
                              {item.topicName}
                            </Badge>
                            <h3 className="text-lg font-semibold"><MathText>{item.question}</MathText></h3>
                          </div>
                          <span className="text-sm text-muted-foreground whitespace-nowrap">
                            {new Date(item.timestamp).toLocaleDateString()}
                          </span>
                        </div>

                        {/* Show/Hide Answer Button */}
                        <Button
                          variant={isRevealed ? "secondary" : "default"}
                          size="sm"
                          onClick={() => toggleRevealAnswer(idx)}
                        >
                          {isRevealed ? (
                            <>
                              <EyeOff className="h-4 w-4 mr-2" />
                              Hide Answer
                            </>
                          ) : (
                            <>
                              <Eye className="h-4 w-4 mr-2" />
                              Show Answer
                            </>
                          )}
                        </Button>

                        {/* Answers (Only shown when revealed) */}
                        {isRevealed && (
                          <div className="space-y-4 animate-in fade-in slide-in-from-top-2">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                              <div className="space-y-2">
                                <div className="flex items-center gap-2 text-sm font-medium text-destructive">
                                  <XCircle className="h-4 w-4" />
                                  Your Answer
                                </div>
                                <div className="p-3 rounded-lg bg-destructive/10 border border-destructive/20">
                                  <p className="text-sm"><MathText>{item.yourAnswer}</MathText></p>
                                </div>
                              </div>

                              <div className="space-y-2">
                                <div className="flex items-center gap-2 text-sm font-medium text-green-600 dark:text-green-500">
                                  <CheckCircle2 className="h-4 w-4" />
                                  Correct Answer
                                </div>
                                <div className="p-3 rounded-lg bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-800">
                                  <p className="text-sm"><MathText>{item.correctAnswer}</MathText></p>
                                </div>
                              </div>
                            </div>

                            <div className="pt-2 border-t">
                              <p className="text-sm font-medium mb-2">Explanation:</p>
                              <p className="text-sm text-muted-foreground"><MathText>{item.explanation}</MathText></p>
                            </div>
                          </div>
                        )}
                      </div>
                    </Card>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
